import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import jwtDecode from 'jwt-decode';
import { CacheResolverService } from '../cache/cache-resolver.service';
import { BaseService } from './base.service';
import { EventsService } from '../../modules/events/events.service';
import { IResponse } from '../../shared/models/app';
import { User, UserDetails, UserPermission } from '../../shared/models/user';
import { TokenService } from '../../shared/utils/token.service';
import { CacheConstant } from '../cache/cache.constant';
import { UserProperty } from '../constants/app.constant';
import { Router } from '@angular/router';
import { userApiUrl } from '../../modules/events/serviceUrl.constant';
import { EncryptionService } from './encryption.service';

@Injectable({
  providedIn: 'root',
})

export class UserService extends BaseService {
  userProfileLoaded$: Subject<boolean> = new Subject<boolean>();
  userDetails = new BehaviorSubject<UserDetails>(null);
  userDetails$ = this.userDetails.asObservable();
  userRole = '';
  isExternal: boolean = true;
  userPermissionFlags = {

  }
  public mailId: string = '';

  constructor(
    protected http: HttpClient,
    private readonly cacheService: CacheResolverService,
    private tokenService: TokenService,
    private eventService: EventsService,
    private router: Router,
    private encryptionService: EncryptionService
  ) {
    super(http);
  }

  init(): void {
    const token = this.cacheService.get(CacheConstant.OktaAuth);
    if (token) {
      this.onUserDetailsLoaded(token.access_token);
      this.userProfileLoaded$.next(true);
    }
  }

  initUserDetailsAndUserRole(): void {
    const token = this.cacheService.get(CacheConstant.OktaAuth);
    if (token) {
      this.onUserDetailsAndUserRole(token.access_token);
      this.userProfileLoaded$.next(true);
    }
  }

  setUserDetails(userDetails: UserDetails) {
    this.userDetails.next(userDetails);
  }

  getUserPermissions(): UserPermission[] {
    const user: User = this.cacheService.get(CacheConstant.UserAuth);
    if (user) {
      const userPermissions: UserPermission[] = user.permissions;
      return userPermissions;
    }
  }

  getDetails(): UserDetails {
    const user: User = this.cacheService.get(CacheConstant.UserAuth);
    let userDetail: UserDetails;

    if (user && user[UserProperty.Detail]) {
      userDetail = user[UserProperty.Detail];
    }

    return userDetail;
  }

  get() {
    return super.fetch('url').pipe(
      map((response: IResponse) => {
        if (!response.hasError) {
          const user: User = {
            detail: response.data.userDetail,
            permissions: response.data.permissionList,
          };
          this.cacheService.set(CacheConstant.UserAuth, user);
          this.userProfileLoaded$.next(true);
        }
        return response;
      })
    );

  }

  private onUserDetailsLoaded(token: string): User {
    const userInfoAndClaims = this.getUserInfoAndClaims(token);
    const info = userInfoAndClaims.info;
    const claims = userInfoAndClaims.claims;
    const user = userInfoAndClaims.user;
    this.getUserRole(claims.iiqUserId, info.email);
    this.saveUserAuth(user);
    return user;
  }

  private onUserDetailsAndUserRole(token: string): User {
    const userInfoAndClaims = this.getUserInfoAndClaims(token);
    const claims = userInfoAndClaims.claims;
    const user = userInfoAndClaims.user;
    this.eventService.getApiRequest('user', userApiUrl.getUserDetails, claims.iiqUserId).toPromise().then(res => {
      this.userRole = res['userRole']['abbreviation'];
      this.cacheService.set('hrmGroup', res['userRole']['hrmGroup']);
      this.cacheService.set('userRole', this.userRole);
    })
    this.saveUserAuth(user);
    return user;
  }

  private saveUserAuth(user: User) {
    this.cacheService.set('user-auth', user, 60 * 60 * 1);
  }

  private getUserInfoAndClaims(token: string) {
    const user: User = {
      detail: null,
      permissions: []
    };
    const userToken = jwtDecode<any>(token);
    const claims = JSON.parse(userToken.user_claims);
    this.isExternal = claims.roleName === 'External';
    const info = JSON.parse(userToken.userinfo);

    user.permissions = claims.roleNums ? claims.roleNums : [];
    user.detail = {
      emailId: info.email,
      emailVerified: info.email_verified,
      firstName: info.given_name,
      lastName: info.family_name,
      loginId: info.preferred_username,
      name: info.name,
      timezone: info.zoneinfo,
      userId: info.sub,
      iiqUserId: claims.iiqUserId
    };
    return {
      user: user,
      info: info,
      claims: claims
    };
  }

  getUserRole(id, mailId) {
    this.eventService.getApiRequest('user', userApiUrl.getUserDetails, id).toPromise().then(res => {
      this.userRole = res['userRole']['abbreviation'];
      this.cacheService.set('userRole', this.userRole);
      this.cacheService.set('hrmGroup', res['userRole']['hrmGroup']);
      if (res['userRole']['abbreviation'] === 'Event Receiver') {
        this.router.navigate(['/dashboard/event-receiver']);
      } else {
        const emailUrl = this.encryptionService.decryptData(localStorage.getItem("email-query-params"));
        if (emailUrl) {
          this.router.navigateByUrl('/dashboard' + emailUrl);
        } else {
          this.router.navigate(['/dashboard']);
        }
        this.cacheService.remove('email-query-params');
      }
    })
  }
}
